import { useCallback, useLayoutEffect, useMemo, useState } from "react";

import { useHistory } from "react-router-dom";
import { InAppNotificationType } from "@doitintl/cmp-models";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import Typography from "@mui/material/Typography";

import { BottomAppBar } from "../../../../Components/BottomAppBar";
import { useErrorSnackbar, useSuccessSnackbar } from "../../../../Components/SharedSnackbar/SharedSnackbar.context";
import { useCustomerContext } from "../../../../Context/CustomerContext";
import { useInAppNotifications } from "../../../../Context/inAppNotifications/InAppNotificationsContextProvider";
import { useUserContext } from "../../../../Context/UserContext";
import texts from "../texts";
import InAppNotificationOption from "./InAppNotificationOption";

export const cyIds = {
  title: "in-app-notification-settings-title",
  description: "in-app-notification-settings-description",
  realtime: {
    title: "in-app-notification-settings-realtime-title",
    description: "in-app-notification-settings-realtime-description",
    toggle: "in-app-notification-settings-realtime-toggle",
    toggleLabel: "in-app-notification-settings-realtime-toggle-label",
    preview: "in-app-notification-settings-realtime-preview",
  },
  buttons: {
    save: "in-app-notification-settings-save",
    cancel: "in-app-notification-settings-cancel",
    back: "in-app-notification-settings-back",
  },
};

function InAppNotificationSettings() {
  const { customer } = useCustomerContext();
  const { userModel } = useUserContext();
  const { showDemoCostAnomaly } = useInAppNotifications();
  const errorSnackbar = useErrorSnackbar();
  const successSnackbar = useSuccessSnackbar();
  const history = useHistory();

  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [selectedTypes, setSelectedTypes] = useState<InAppNotificationType[]>([]);
  const [allowRealTime, setAllowRealTime] = useState<boolean>(false);

  const isDisabled = useMemo<boolean>(() => isSaving || !userModel?.ref, [isSaving, userModel?.ref]);

  // Initialize state from user model
  useLayoutEffect(() => {
    if (userModel?.inAppNotificationSettings) {
      setSelectedTypes(userModel.inAppNotificationSettings.notificationTypes);
      setAllowRealTime(userModel.inAppNotificationSettings.allowRealTime);
    }
  }, [userModel]);

  const handleBack = useCallback<() => void>(() => {
    history.push(`/customers/${customer.id}/notifications`);
  }, [history, customer.id]);

  const handleNotificationTypeChange = useCallback<(type: InAppNotificationType, checked: boolean) => void>(
    (type, checked) => {
      setSelectedTypes((prev) => (checked ? [...prev, type] : prev.filter((t) => t !== type)));
    },
    []
  );

  const handleCancel = useCallback<() => void>(() => {
    if (!userModel?.inAppNotificationSettings) {
      setSelectedTypes([]);
      setAllowRealTime(true);
      return;
    }

    setSelectedTypes(userModel.inAppNotificationSettings.notificationTypes);
    setAllowRealTime(userModel.inAppNotificationSettings.allowRealTime);
  }, [userModel]);

  const handleSave = useCallback<() => Promise<void>>(async () => {
    if (!userModel?.ref) {
      return;
    }

    setIsSaving(true);
    try {
      await userModel.ref.update({
        inAppNotificationSettings: {
          notificationTypes: selectedTypes,
          allowRealTime,
        },
      });

      setIsSaving(false);
      successSnackbar(texts.settings.snackbars.success);
      history.push(`/customers/${customer.id}/notifications`);
    } catch (error) {
      errorSnackbar(texts.settings.snackbars.error);
      setIsSaving(false);
    }
  }, [userModel, selectedTypes, allowRealTime, history, errorSnackbar, successSnackbar, customer.id]);

  return (
    <>
      <Container maxWidth="md" sx={{ pt: 4, pb: 8 }}>
        <Stack gap={4}>
          <Typography variant="h3" data-cy={cyIds.title}>
            {texts.settings.selection.title}
          </Typography>
          <FormGroup>
            <Typography variant="body1" data-cy={cyIds.description}>
              {texts.settings.selection.description}
            </Typography>
            <InAppNotificationOption
              type={InAppNotificationType.CostAnomaly}
              checked={selectedTypes.includes(InAppNotificationType.CostAnomaly)}
              onChange={handleNotificationTypeChange}
            />
            <InAppNotificationOption
              type={InAppNotificationType.CloudIncident}
              checked={selectedTypes.includes(InAppNotificationType.CloudIncident)}
              onChange={handleNotificationTypeChange}
            />
            <InAppNotificationOption
              type={InAppNotificationType.Alert}
              checked={selectedTypes.includes(InAppNotificationType.Alert)}
              onChange={handleNotificationTypeChange}
            />
            <InAppNotificationOption
              type={InAppNotificationType.Budget}
              checked={selectedTypes.includes(InAppNotificationType.Budget)}
              onChange={handleNotificationTypeChange}
            />
            <InAppNotificationOption
              type={InAppNotificationType.NewInvoice}
              checked={selectedTypes.includes(InAppNotificationType.NewInvoice)}
              onChange={handleNotificationTypeChange}
            />
            <InAppNotificationOption
              type={InAppNotificationType.InvoiceReminder}
              checked={selectedTypes.includes(InAppNotificationType.InvoiceReminder)}
              onChange={handleNotificationTypeChange}
            />
          </FormGroup>
          <Stack gap={1}>
            <Typography variant="subtitle1" data-cy={cyIds.realtime.title}>
              {texts.settings.realTime.title}
            </Typography>
            <Typography variant="body2" data-cy={cyIds.realtime.description}>
              {texts.settings.realTime.description}
            </Typography>
            <FormControlLabel
              data-cy={cyIds.realtime.toggleLabel}
              control={
                <Switch
                  data-cy={cyIds.realtime.toggle}
                  checked={allowRealTime}
                  onChange={(e) => {
                    setAllowRealTime(e.target.checked);
                  }}
                />
              }
              label={texts.settings.realTime.toggleLabel}
            />
            <Button
              variant="contained"
              color="primary"
              sx={{ width: "fit-content" }}
              data-cy={cyIds.realtime.preview}
              onClick={showDemoCostAnomaly}
            >
              {texts.settings.realTime.preview}
            </Button>
          </Stack>
        </Stack>
      </Container>
      <BottomAppBar
        primaryButtonName={texts.settings.buttons.save}
        primaryButtonProps={{
          "data-cy": cyIds.buttons.save,
        }}
        handlePrimaryButtonClicked={handleSave}
        secondaryButtonName={texts.settings.buttons.cancel}
        secondaryButtonProps={{
          "data-cy": cyIds.buttons.cancel,
        }}
        handleSecondaryButtonClicked={handleCancel}
        backButtonName={texts.settings.buttons.back}
        backButtonProps={{
          "data-cy": cyIds.buttons.back,
        }}
        handleBackButtonClicked={handleBack}
        disabled={isDisabled}
        loading={isSaving}
        maxWidth="md"
        stackPadding={"right"}
      />
    </>
  );
}

export default InAppNotificationSettings;
